import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Application} from "../Application";
import {Container} from "../core/Container";
import {Callback} from "../core/Reflection";
import {SlotId} from "../data/StoryModel";
import {BottomHud} from "../gameobjects/BottomHud";
import {Button} from "../gameobjects/Button";
import {Carousel} from "../gameobjects/Carousel";
import {Focusables} from "../gameobjects/Focusables";
import {MuteButton} from "../gameobjects/MuteButton";
import {OnboardingArrow} from "../gameobjects/OnboardingArrow";
import {SceneCreatorCarouselButton} from "../gameobjects/SceneCreatorCarouselButton";
import {SceneCreatorScene} from "../gameobjects/SceneCreatorScene";
import {SceneCreatorLayout_main} from "../layout/SceneCreatorLayout";
import {GameManager} from "../managers/GameManager";
import {VoiceOverManager} from "../managers/VoiceOverManager";
import {PaperBackgroundState} from "./PaperBackgroundState";

export class SceneCreator extends PaperBackgroundState {

    public static readonly NAME = "StandaloneFreePlayAlt";

    public readonly main = this.addChild(new Container());
    public readonly scene = this.main.addChild(new SceneCreatorScene());
    public readonly items = this.main.addChild(new Items());
    public readonly hud = this.main.addChild(new Hud());
    public readonly buttonMute = this.addChild(new MuteButton());

    private isCarouselAnimating = false;
    private selectedSlotId?: SlotId;
    private readonly onboardingArrows: OnboardingArrow[] = [];

    public init(pSize: PIXI.Point) {
        super.init(pSize);

        SceneCreatorLayout_main.call(this);
        this.main.scale.set(0.5);

        this.items.carousel.verticalAnchor = 1;
        this.hud.buttonHome.onClick.add(() => {
            GameManager.instance.clearScenes();
            this.gotoHomeState();
        });
        this.hud.buttonRandom.onClick.add(() => {
            this.hideCarousel();
            this.scene.randomizeOptions();
        });
        this.scene.onSelectSlot.add((slotId) => {
            this.hideOnboardingArrows();
            if (this.isCarouselAnimating || this.selectedSlotId === slotId) {
                return;
            }
            this.isCarouselAnimating = true;
            this.selectedSlotId = slotId;
            this.items.hide(() => {
                Focusables.remove(
                    (it) => it instanceof SceneCreatorCarouselButton,
                    this.items.carousel.buttonNext,
                    this.items.carousel.buttonPrev);
                this.items.carousel.setItems(GameManager.instance.getOptions(slotId)
                    .filter((optionId) => !this.scene.isSameCharacter(optionId, slotId))
                    .map((optionId) => new SceneCreatorCarouselButton(slotId, optionId, () => {
                        GameManager.instance.selectOption(optionId, slotId);
                        this.scene.updateOptions();
                        if (slotId === "character") {
                            this.scene.onSelectSlot("outfit");
                        }
                        if (slotId === "characterOther") {
                            this.scene.onSelectSlot("outfitOther");
                        }
                    })));
                Focusables.add(
                    ...this.items.carousel.getItems(),
                    this.items.carousel.buttonNext,
                    this.items.carousel.buttonPrev);
                PubSub.publishSync(HLF.FORCE_FOCUS, this.items.carousel.getItem(0, true));
                this.items.show(() => {
                    this.isCarouselAnimating = false;
                });
            });
        });

        this.bg.interactive = true;
        this.bg.on("pointertap", () => {
            this.hideCarousel();
        });

        Focusables.add(
            this.hud.buttonHome,
            this.hud.buttonRandom,
            this.buttonMute,
            ...this.scene.getSceneSlotButtons(),
        );

        const isOnboarding = !Application.instance.saveManager.readBool("scene-creator-visited");

        this.lockScreen();
        this.shuffleOptions(8, () => {
            this.unlockScreen();
            VoiceOverManager.instance.playVO("freeplay-tweaked");
            if (isOnboarding) {
                this.showOnboardingArrow("character");
                this.showOnboardingArrow("characterOther");
            }
        });

        Application.instance.saveManager.storeBool("scene-creator-visited", true);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        this.buttonMute.setPosition(pSize);
    }

    private shuffleOptions(iterations: number, cb?: Callback) {
        this.scene.randomizeOptions();
        this.scene.alpha = 0;
        this.scene.alphaTo(1, 0.04, () => {
            if (iterations > 0) {
                this.scene.alphaTo(0, 0.04, {
                    delay: 0.2,
                    onComplete: () => {
                        this.shuffleOptions(iterations - 1, cb);
                    },
                });
            } else if (cb) {
                cb();
            }
        });
    }

    private hideCarousel() {
        this.isCarouselAnimating = false;
        this.selectedSlotId = undefined;
        this.items.hide();
    }

    private showOnboardingArrow(slotId: SlotId) {
        const character = this.scene.getSceneSlot(slotId);
        if (character) {
            const arrow = new OnboardingArrow();
            character.parent.addChild(arrow);
            arrow.x = character.x;
            arrow.y = character.y - character.button!.height;
            arrow.changeParent(this.main);
            arrow.alphaFrom(0, 0.2);
            this.onboardingArrows.push(arrow);
        }
    }

    private hideOnboardingArrows() {
        while (this.onboardingArrows.length > 0) {
            const arrow = this.onboardingArrows.pop()!;
            arrow.alphaTo(0, 0.2, () => {
                arrow.destroy();
            });
        }
    }
}

class Hud extends Container {

    public readonly buttonHome = this.addChild(new Button());
    public readonly buttonRandom = this.addChild(new Button());
}

class Items extends BottomHud {

    public readonly carousel = this.addChild(new Carousel<SceneCreatorCarouselButton>(5, 5));
}
